import { Pencil, Trash2, Clock, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import type { AssistantCron } from '../../types/assistantCron';
import { INTERVAL_LABELS } from '../../types/assistantCron';
import { formatDateTime } from '../../lib/formatters';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  EmptyState,
} from '../ui';

interface AssistantCronListProps {
  crons: AssistantCron[];
  onEdit: (cron: AssistantCron) => void;
  onDelete: (cron: AssistantCron) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function AssistantCronList({
  crons,
  onEdit,
  onDelete,
  isLoading,
  isFiltered,
}: AssistantCronListProps) {
  const getStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="default">Nunca executado</Badge>;
    switch (status.toLowerCase()) {
      case 'success':
      case 'succeeded':
        return <Badge variant="success">Sucesso</Badge>;
      case 'failed':
      case 'error':
        return <Badge variant="error">Falhou</Badge>;
      case 'running':
        return <Badge variant="info">Executando</Badge>;
      default:
        return <Badge variant="warning">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string | null) => {
    if (!status) return null;
    switch (status.toLowerCase()) {
      case 'success':
      case 'succeeded':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="h-3.5 w-3.5 text-red-500" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (crons.length === 0) {
    return (
      <EmptyState
        icon={<Clock className="h-12 w-12" />}
        title={isFiltered ? 'Nenhum cron encontrado' : 'Nenhum cron disponível'}
        description={
          isFiltered
            ? 'Tente ajustar sua pesquisa para encontrar o que procura'
            : 'Crie seu primeiro cron para começar'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Assistente</TableHead>
            <TableHead>Intervalo</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Última Execução</TableHead>
            <TableHead>Próxima Execução</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {crons.map(cron => (
            <TableRow key={cron.id}>
              <TableCell>{cron.id}</TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{cron.name}</span>
                  {!cron.enabled && <Badge variant="default">Inativo</Badge>}
                </div>
              </TableCell>
              <TableCell>{cron.assistant_name || '-'}</TableCell>
              <TableCell>{INTERVAL_LABELS[cron.interval_minutes] || `${cron.interval_minutes} min`}</TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(cron.last_status)}
                    {getStatusBadge(cron.last_status)}
                  </div>
                  {cron.last_message && (
                    <div className="mt-1 flex items-start gap-1 text-xs font-medium text-gray-900 dark:text-gray-100">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span className="break-words">{cron.last_message}</span>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{cron.last_run_at ? formatDateTime(cron.last_run_at) : '-'}</TableCell>
              <TableCell>{cron.next_run_at ? formatDateTime(cron.next_run_at) : '-'}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(cron)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(cron)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default AssistantCronList;
